import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from 'react-router-dom';

import { MOBILE_MAX_WIDTH_LAYOUT } from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box, Flex, Heading } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import PhotoGallerySlider from '@rover/react-lib/src/components/images/PhotoGallery/PhotoGallerySlider';
import Alert from '@rover/react-lib/src/components/notifications/Alert/Alert';
import DjangoMessages from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/components/DjangoMessages';
import {
  FORCE_SEO_LANDING,
  NEARBY_TRAINER_COUNT,
} from '@rover/react-lib/src/pages/training/constants/params';
import { useApiBundleOfferingList } from '@rover/rsdk/src/apiClient/latest/api-bundle-offering-list';
import { useApiPublicTrainerProfileRetrieve } from '@rover/rsdk/src/apiClient/latest/api-public-trainer-profile-retrieve';
import type { PersonTrainerProfile } from '@rover/rsdk/src/apiClient/latest/model/personTrainerProfile';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import {
  FLAGS,
  useFrontendConfiguration,
} from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import getUserAgentFlags from '@rover/shared/js/utils/getUserAgentFlags';
import emitMetric from '@rover/utilities/emitMetric';
import { Http403Error } from '@rover/utilities/HttpError';

import FloatingFooter from '../../../components/utils/FloatingFooter';
import useMatchMedia from '../../../hooks/useMatchMedia';
import getQueryError from '../../../utils/getQueryError';
import ReportProfileSection from '../../sitter-profile/SitterProfilePage/components/ReportProfileSection/ReportProfileSection';
import { FullPageLoading } from '../components';
import { withRolloutTrainerSignUpGlobal } from '../components/rollout-guards';
import { TrainerPublicProfilePageView } from '../events/analyticsEvents';
import useInTrainingPilot from '../hooks/useInTrainingPilot';
import { decodeSkillAndBehaviorQueryParams } from '../utils/skillAndBehaviorQueryParams';

import { AboutSection } from './components/AboutSection/AboutSection';
import ContactButton from './components/ContactButton/ContactButton';
import { ReviewsModalContextProvider } from './components/FullMixedReviewsList/ReviewsModalContext';
import { GuaranteeBanner } from './components/GuaranteeBanner';
import MixedReviewsSection from './components/MixedReviewsSection/MixedReviewsSection';
import ProfileContentReorderedSections from './components/ProfileContentReorderedSections';
import { AvailabilitySection } from './components/ServiceBox/AvailabilitySection';
import PriceTransparencyNotice from './components/ServiceBox/PriceTransparencyNotice';
import TrainingServiceHeader from './components/ServiceBox/TrainingServiceHeader';
import ShowMoreTrainersLink from './components/ShowMoreTrainersLink';
import TrainerInformation from './components/TrainerInformation/TrainerInformation';
import TrainerPhotoGallery from './components/TrainerPhotoGallery/TrainerPhotoGallery';
import VirtualTrainingBox from './components/VirtualTrainingBox/VirtualTrainingBox';
import WhatToExpectSection from './components/WhatToExpectSection';
import { publicTrainerProfileDataPrefetch } from './utils/prefetch';
import useTrainerImages from './utils/useTrainerImages';
import { TRAINER_PROFILE_SEO_META_DESCRIPTION } from './constants';
import {
  AvailabilityLayout,
  GalleryLayout,
  HeaderContainerLayout,
  HeaderLayout,
  LeftColumnLayout,
  MoreInfoLayout,
  MoreSittersLayout,
  PageLayout as PublicTrainerProfilePageLayout,
  PricingAvailabilityLayout,
  ProfileLayout,
  SectionWithBottomSeparator,
  ServiceLayout,
  SkillsAndRestDesktopLayout,
  SkillsAndRestTableLayout,
} from './PublicTrainerProfilePageLayout';

export interface MatchParams {
  slug: string;
  [NEARBY_TRAINER_COUNT]?: string;
  [FORCE_SEO_LANDING]?: string;
}

const PublicTrainerProfilePage = ({
  match,
  location,
}: RouteComponentProps<MatchParams>): JSX.Element => {
  const theme = useTheme();
  const { slug } = match.params;
  const queryParams = location.search;

  const headerLayoutRef = useRef<HTMLDivElement | null>(null);

  const [footerHeight, setFooterHeight] = useState(0);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedGalleryImageIndex, setSelectedGalleryImageIndex] = useState(0);

  const skillsAndBehaviorSlugs = decodeSkillAndBehaviorQueryParams(queryParams);

  const { isFlagActive, isLoading, error: frontendConfigurationError } = useFrontendConfiguration();

  if (frontendConfigurationError) {
    throw getQueryError(frontendConfigurationError);
  }

  const bundleChangesActive =
    isFlagActive(FLAGS.ROLLOUT_PROVIDER_BUNDLE_OFFERINGS) &&
    !isFlagActive(FLAGS.KILLSWITCH_TRAINING_BUNDLE_VIEW_OFFERINGS);
  const virtualTrainingV1Active = isFlagActive(FLAGS.ROLLOUT_VIRTUAL_TRAINING_V1);

  const isTrainerProfileReordering = isFlagActive(FLAGS.ROLLOUT_PUBLIC_TRAINER_PROFILE_REORDERING);

  const onCloseLightbox = (): void => {
    setIsLightboxOpen(false);
  };

  const dismissError = (): void => {
    setError(undefined);
  };

  const dismissSuccess = (): void => {
    setSuccess(undefined);
  };

  const showSuccess = (successDescription: string): void => {
    setSuccess(successDescription);
  };

  // Fire custom event with consent purpose 'advertising' for optimizing ad spend
  // (in addition to the default pageview)
  useEffect(() => {
    emitAnalyticsEvent(new TrainerPublicProfilePageView());
  }, []);

  useEffect(() => {
    // If the observer is undefined, exit early and don't init them
    if (typeof ResizeObserver === 'undefined') {
      return () => {};
    }

    const resizeObserver = new ResizeObserver(() => {
      if (headerLayoutRef.current) {
        setFooterHeight(headerLayoutRef.current.clientHeight);
      }
    });

    if (process.env.JS_ENV_CLIENT && headerLayoutRef.current) {
      resizeObserver.observe(headerLayoutRef.current);
    }
    return (): void => {
      if (process.env.JS_ENV_CLIENT) {
        resizeObserver.disconnect();
      }
    };
  }, [headerLayoutRef]);

  const currentUser = useCurrentUser();
  const { data: trainerInfo }: { data: PersonTrainerProfile | undefined } =
    useApiPublicTrainerProfileRetrieve(slug);

  const { data: bundleOfferinglist = [] } = useApiBundleOfferingList(slug, 'dog-training', {
    query: {
      enabled: bundleChangesActive && !!slug,
    },
  });

  const {
    data: trainerImages,
    count: imagesCount,
    onPaginate,
  } = useTrainerImages({ opk: trainerInfo?.opk });

  const { inTrainingPilot } = useInTrainingPilot();

  const matchesDesktopMedia = useMatchMedia(MQ.MD_UP.getBreakpointString());
  const { isDesktop } = getUserAgentFlags();

  if (!trainerInfo) {
    return <Box data-testid="no-trainer-info" />;
  }

  const { trainingServicePreferences, service, shouldUsePriceTransparency, trainerInsights } =
    trainerInfo;

  const { trainingConsultationPreferences } = trainingServicePreferences;

  const { details, education, isApproved, virtualOnly } = trainerInfo.trainerProfile;

  const ownProfile = currentUser.user?.opk === trainerInfo.opk;

  const handleImageClick = (index: number, fromAvatar: boolean): void => {
    setSelectedGalleryImageIndex(index);
    setIsLightboxOpen(true);
    if (fromAvatar) {
      emitMetric('training.profile.avatar_clicked', { index: `${index}` });
      Logger.info('Public Trainer Profile avatar clicked', { index: `${index}` });
    } else {
      emitMetric('training.profile.gallery_clicked', { index: `${index}` });
      Logger.info('Public Trainer Profile gallery clicked', { index: `${index}` });
    }
  };

  const showContactButton = !ownProfile && isApproved && inTrainingPilot;
  const showVirtualBadging = virtualTrainingV1Active && virtualOnly;
  const showConsultationText = trainingConsultationPreferences.length > 0;
  const showMoreTrainersLink = isFlagActive(FLAGS.ROLLOUT_PUBLIC_TRAINER_PROFILE_SEO);

  const hideNoIndexTag =
    isFlagActive(FLAGS.ROLLOUT_PUBLIC_TRAINER_PROFILE_SEO) && service.trainingSearchable;

  if (isLoading) {
    return <FullPageLoading />;
  }

  // TODO (DEV-104195): Translate below text after training pilot launch
  const titleLocationText = virtualOnly ? 'Virtual trainer' : trainerInfo.service.location;
  const standardSessionPrice =
    shouldUsePriceTransparency && trainingServicePreferences.fullPricePerSession !== null
      ? trainingServicePreferences.fullPricePerSession
      : trainingServicePreferences.pricePerSession;

  return (
    <ReviewsModalContextProvider>
      <Helmet>
        {!hideNoIndexTag && <meta name="robots" content="noindex" />}
        <meta name="description" content={TRAINER_PROFILE_SEO_META_DESCRIPTION} />
        <title>
          {trainerInfo.shortName} | {titleLocationText} Rover.com
        </title>
      </Helmet>

      <FloatingFooter.ScrollBoundary
        offsetTop={
          (matchesDesktopMedia === undefined && isDesktop) || matchesDesktopMedia
            ? // Trigger sticky footer 80px earlier to ensure a CTA button is always visible.
              // Without this offset, there's a gap where the header CTA scrolls away
              // before the sticky footer appears at the bottom.
              `calc(100vh + ${footerHeight}px - 80px)`
            : '0'
        }
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          width="100%"
          pt={['0x', '8x']}
          alignSelf="stretch"
        >
          <Flex flexDirection="column" width={['100%', '750px', '970px', '1170px']}>
            <HeaderContainerLayout>
              <HeaderLayout ref={headerLayoutRef}>
                <ProfileLayout>
                  <Box paddingBottom={showContactButton ? ['0x', '0x', '6x'] : '0x'}>
                    <TrainerInformation
                      badges={trainerInfo.badges}
                      location={service.location}
                      defaultImage={trainerInfo.defaultImage}
                      shortName={trainerInfo.shortName}
                      ownProfile={ownProfile}
                      onAvatarClick={(index) => handleImageClick(index, true)}
                      isApproved={isApproved}
                      inTrainingPilot={inTrainingPilot}
                      justifyContent="flex-start"
                      opk={trainerInfo.opk}
                      trainerInsights={trainerInsights}
                      virtualOnly={virtualOnly}
                      flair={trainerInfo.flair}
                    />
                  </Box>
                  {showContactButton && (
                    <Flex pt="6x" width="100%" display={['none', 'none', 'flex']}>
                      <ContactButton
                        shortName={trainerInfo.shortName}
                        skillsAndBehaviorSlugs={skillsAndBehaviorSlugs}
                        location={location}
                      />
                    </Flex>
                  )}
                  {showMoreTrainersLink && (
                    <ShowMoreTrainersLink
                      zip={trainerInfo.service.zip}
                      providerSlug={slug}
                      pt="6x"
                      display={['none', 'none', 'flex']}
                    />
                  )}
                  {showVirtualBadging && (
                    <VirtualTrainingBox
                      shortName={trainerInfo.shortName}
                      display={['flex', 'flex', 'none']}
                      flexDirection={['column', 'row']}
                      mt={['10x', '8x']}
                    />
                  )}
                </ProfileLayout>
                <GalleryLayout>
                  {trainerImages.length > 0 && (
                    <TrainerPhotoGallery
                      images={trainerImages}
                      onImageClick={(index) => handleImageClick(index, false)}
                      count={imagesCount}
                    />
                  )}
                </GalleryLayout>
              </HeaderLayout>
              {isLightboxOpen && (
                <PhotoGallerySlider
                  images={[trainerInfo.defaultImage, ...trainerImages]}
                  index={selectedGalleryImageIndex}
                  handleOnCloseLightbox={onCloseLightbox}
                  onReachEnd={onPaginate}
                />
              )}
            </HeaderContainerLayout>
            <PublicTrainerProfilePageLayout>
              <Flex
                flexDirection={['column', 'column', 'row']}
                sx={{
                  pt: [
                    isTrainerProfileReordering ? theme.space['10x'] : theme.space['6x'],
                    '0px',
                    theme.space['12x'],
                  ],
                }}
              >
                <Flex
                  flex="1"
                  sx={{ order: [1, 1, 2] }}
                  flexDirection="column"
                  width={['100%', '100%', 'initial']}
                  marginLeft={['0x', '0x', '6x']}
                >
                  <Box />
                  <SectionWithBottomSeparator>
                    <AboutSection
                      behaviors={trainerInfo.trainerProfile.behaviors}
                      behaviorsCustom={trainerInfo.trainerProfile.behaviorsCustom}
                      headline={details.headline}
                      methodology={details.methodology}
                      methodologyCustom={details.methodologyCustom || undefined}
                      methodologyDetails={details.methodologyDetails || undefined}
                      yearsOfTraining={details.yearsOfTraining}
                      educationList={education}
                      description={details.experienceDetails}
                      name={trainerInfo.shortName}
                      defaultImage={trainerInfo.defaultImage}
                      skills={trainerInfo.trainerProfile.skills}
                      skillsCustom={trainerInfo.trainerProfile.skillsCustom}
                      variant={isTrainerProfileReordering ? 'simplified' : 'full'}
                    />
                  </SectionWithBottomSeparator>

                  {!isTrainerProfileReordering && <MixedReviewsSection trainerInfo={trainerInfo} />}

                  <SkillsAndRestDesktopLayout>
                    {isTrainerProfileReordering ? (
                      <ProfileContentReorderedSections trainerInfo={trainerInfo} />
                    ) : (
                      <MoreInfoLayout>
                        <WhatToExpectSection trainerInfo={trainerInfo} />
                      </MoreInfoLayout>
                    )}
                  </SkillsAndRestDesktopLayout>
                </Flex>

                <LeftColumnLayout>
                  {showVirtualBadging && (
                    <VirtualTrainingBox
                      shortName={trainerInfo.shortName}
                      display={['none', 'none', 'flex']}
                      mb="6x"
                    />
                  )}
                  <PricingAvailabilityLayout>
                    <Flex
                      flexDirection={['column', 'row', 'column']}
                      alignItems={['initial', 'flex-start', 'initial']}
                      width="100%"
                      sx={{ pb: ['0px'] }}
                    >
                      <ServiceLayout>
                        <Flex flexDirection="column" mb="6x">
                          <Heading webProps={{ responsive: false }} size="400" mb="2x">
                            {/* TODO (DEV-104195): Translate below text after training pilot launch */}
                            Services
                          </Heading>
                          {shouldUsePriceTransparency && <PriceTransparencyNotice />}
                        </Flex>
                        <TrainingServiceHeader
                          rate={standardSessionPrice}
                          consultations={trainingServicePreferences.trainingConsultationPreferences}
                          bundleOfferings={bundleOfferinglist}
                          bundleChangesActive={bundleChangesActive}
                          showVirtualBadging={showVirtualBadging}
                          shouldUsePriceTransparency={shouldUsePriceTransparency}
                        />
                      </ServiceLayout>
                      <AvailabilityLayout>
                        <AvailabilitySection
                          availabilityDelay={trainingServicePreferences.availabilityDelay}
                          availabilityPreferences={service.availabilityPreferences}
                          availabilityDetails={details.availabilityDetails}
                          cancellationPolicyDescription={service.cancellationPolicyDesc}
                          cancellationPolicyTranslated={service.cancellationPolicyTranslated}
                        />
                      </AvailabilityLayout>
                    </Flex>
                    <Box
                      borderTopWidth={['1px', '1px', '0px']}
                      borderColor={theme.colors.separator.primary}
                      borderTopStyle="solid"
                      sx={{
                        pb: [theme.space['10x'], theme.space['10x'], '0px'],
                      }}
                    />
                    <SkillsAndRestTableLayout>
                      {isTrainerProfileReordering ? (
                        <ProfileContentReorderedSections trainerInfo={trainerInfo} />
                      ) : (
                        <WhatToExpectSection trainerInfo={trainerInfo} />
                      )}
                    </SkillsAndRestTableLayout>
                  </PricingAvailabilityLayout>

                  {!ownProfile && isApproved && (
                    <Flex paddingY="10x" width="100%" justifyContent="center" marginTop="6x">
                      <ReportProfileSection
                        isLoggedIn={currentUser.isAuthenticated}
                        location={location}
                        onSuccess={showSuccess}
                      />
                    </Flex>
                  )}
                </LeftColumnLayout>
              </Flex>
            </PublicTrainerProfilePageLayout>
          </Flex>
          {(error || success) && (
            <Flex
              zIndex="1000"
              position="fixed"
              top={theme.space['10x']}
              left={['none', 'none', theme.space['10x']]}
              textAlign="center"
              justifyContent="center"
            >
              {error && (
                <Alert
                  severity={Alert.Severity.DANGER}
                  onDismiss={dismissError}
                  mb="0x"
                  data-testid="error-display"
                >
                  {error}
                </Alert>
              )}
              {success && (
                <Alert
                  severity={Alert.Severity.SUCCESS}
                  onDismiss={dismissSuccess}
                  mb="0x"
                  data-testid="success-display"
                >
                  {success}
                </Alert>
              )}
            </Flex>
          )}
        </Flex>

        <MoreSittersLayout>
          <GuaranteeBanner />
        </MoreSittersLayout>

        <FloatingFooter background="primary">
          <Flex maxWidth={['100%']} justifyContent="center">
            <Flex
              width="100%"
              justifyContent={['center', 'space-between']}
              flexDirection={['column', 'row']}
              alignItems="center"
              maxWidth={[MOBILE_MAX_WIDTH_LAYOUT, 'initial']}
            >
              <Box display={['none', 'flex']} flex={['none', '1']}>
                <TrainerInformation
                  badges={trainerInfo.badges}
                  location={service.location}
                  defaultImage={trainerInfo.defaultImage}
                  shortName={trainerInfo.shortName}
                  ownProfile={ownProfile}
                  isApproved={isApproved}
                  variant="compact"
                  inTrainingPilot={inTrainingPilot}
                  opk={trainerInfo.opk}
                  virtualOnly={virtualOnly}
                />
              </Box>
              <Flex display={['none', 'flex']}>
                {showMoreTrainersLink && (
                  <ShowMoreTrainersLink zip={trainerInfo.service.zip} providerSlug={slug} />
                )}
              </Flex>
              <Flex flex={['1', 'none']}>
                {showContactButton && (
                  <ContactButton
                    shortName={trainerInfo.shortName}
                    showConsultationText={showConsultationText}
                    location={location}
                    skillsAndBehaviorSlugs={skillsAndBehaviorSlugs}
                  />
                )}
              </Flex>
              <Flex display={['flex', 'none']}>
                {showMoreTrainersLink && (
                  <ShowMoreTrainersLink zip={trainerInfo.service.zip} providerSlug={slug} py="3x" />
                )}
              </Flex>
            </Flex>
          </Flex>
        </FloatingFooter>
      </FloatingFooter.ScrollBoundary>
      <DjangoMessages />
    </ReviewsModalContextProvider>
  );
};

PublicTrainerProfilePage.reactQueryPrefetch = publicTrainerProfileDataPrefetch;
PublicTrainerProfilePage.shouldSetBackHistoryHeader = true;

export default withRolloutTrainerSignUpGlobal(PublicTrainerProfilePage, () => {
  throw new Http403Error('Global trainer sign up is not active!');
});
